import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Users2, Search, ShieldCheck, Loader2, KeyRound, Plus, Trash2, Tag,
  CheckCircle, XCircle, LogOut, BadgeCheck, ClipboardList, X, ArrowRight,
  UserPlus, UserMinus, Activity, TrendingUp, IndianRupee, Package,
  Calendar, ChevronDown, ChevronUp, BarChart3
} from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { id: "overview", label: "Overview", icon: ClipboardList },
  { id: "assign", label: "Assign", icon: Plus },
  { id: "members", label: "Manage", icon: Users2 },
  { id: "revenue", label: "Revenue", icon: BarChart3 },
];

export default function SegmentAdminDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [members, setMembers] = useState([]);
  const [cobrand, setCobrand] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [d, m, c] = await Promise.all([
        fetch(`${API}/sa/dashboard`, { credentials: "include" }).then((r) => r.ok ? r.json() : null),
        fetch(`${API}/sa/members`, { credentials: "include" }).then((r) => r.ok ? r.json() : []),
        fetch(`${API}/sa/cobranding`, { credentials: "include" }).then((r) => r.ok ? r.json() : []),
      ]);
      if (d) { setMe({ segment: d.segment }); setStats(d.stats); setActivity(d.recent_activity || []); }
      setMembers(Array.isArray(m) ? m : []);
      setCobrand(Array.isArray(c) ? c : []);
    } catch {
      toast.error("Failed to load dashboard");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!me?.segment) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6 text-center">
        <Users2 size={32} className="text-gray-300 mb-3" />
        <p className="text-gray-700 font-medium">You're not currently linked to any segment.</p>
        <p className="text-xs text-gray-500 mt-1">Ask your admin to link a segment to your account.</p>
        <Button variant="outline" className="mt-5" onClick={async () => { try { await logout(); } catch {} navigate("/"); }}>
          <LogOut size={14} className="mr-1.5" /> Logout
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="sa-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
              <Users2 size={16} className="text-emerald-600" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wider text-emerald-600 font-bold">Segment Admin</p>
              <h1 className="text-lg font-bold text-gray-900 truncate">{me.segment.name}</h1>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={async () => { try { await logout(); } catch {} navigate("/"); }} data-testid="sa-logout">
            <LogOut size={14} className="mr-1.5" /> Logout
          </Button>
        </div>
        <div className="max-w-5xl mx-auto flex">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold border-b-2 transition-colors ${
                tab === t.id ? "border-emerald-600 text-emerald-700" : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
              data-testid={`sa-tab-${t.id}`}
            >
              <t.icon size={13} /> {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 pb-32">
        {tab === "overview" && <OverviewTab stats={stats} segment={me.segment} cobrand={cobrand} activity={activity} />}
        {tab === "assign" && <AssignTab onAssigned={fetchAll} />}
        {tab === "members" && <MembersTab members={members} onChanged={fetchAll} />}
        {tab === "revenue" && <RevenueTab />}
      </main>
    </div>
  );
}

/* ========== Overview ========== */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveCobrandUrl = (url) => url ? (url.startsWith("http") ? url : `${BACKEND_URL}${url}`) : "";

function OverviewTab({ stats, segment, cobrand, activity }) {
  const cards = [
    { label: "Total Codes", value: stats?.total_codes ?? 0, color: "bg-gray-50 text-gray-900 border-gray-100" },
    { label: "Available", value: stats?.available ?? 0, color: "bg-blue-50 text-blue-700 border-blue-100" },
    { label: "Issued", value: stats?.issued ?? 0, color: "bg-emerald-50 text-emerald-700 border-emerald-100" },
    { label: "Active Members", value: stats?.active_members ?? 0, color: "bg-amber-50 text-amber-700 border-amber-100" },
    { label: "Revoked", value: stats?.revoked ?? 0, color: "bg-red-50 text-red-700 border-red-100" },
  ];

  return (
    <div className="space-y-5" data-testid="sa-overview">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {cards.map((c) => (
          <div key={c.label} className={`rounded-2xl border p-4 ${c.color}`}>
            <p className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{c.label}</p>
            <p className="text-3xl font-extrabold mt-1">{c.value}</p>
          </div>
        ))}
      </div>

      {/* On mobile: About first, Recent Activity below. On md+: Recent Activity first, About below. */}
      <div className="flex flex-col gap-5">
        <div className="order-2 md:order-1">
          <RecentActivityStrip activity={activity} />
        </div>
        <div className="order-1 md:order-2">
          {(segment?.description || cobrand?.length > 0) && (
            <AboutSegmentBlock segment={segment} cobrand={cobrand} />
          )}
        </div>
      </div>
    </div>
  );
}

function AboutSegmentBlock({ segment, cobrand }) {
  return (
    <section data-testid="sa-about-segment" className="space-y-3">
      <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">About this segment</p>
      {segment?.description && (
        <div className="bg-white border border-gray-100 rounded-2xl p-5">
          <p className="text-sm text-gray-700">{segment.description}</p>
        </div>
      )}
      {cobrand?.map((c) => (
        <article
          key={c.cobranding_id}
          className="relative overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm"
          data-testid={`sa-cobranding-card-${c.cobranding_id}`}
        >
          {c.banner_url && (
            <div className="w-full aspect-[16/7] bg-gray-50 overflow-hidden">
              <img
                src={resolveCobrandUrl(c.banner_url)}
                alt={c.name}
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            </div>
          )}
          <div className="p-4 flex items-start gap-3">
            {c.logo_url && (
              <div className="w-12 h-12 rounded-xl border border-gray-100 bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
                <img
                  src={resolveCobrandUrl(c.logo_url)}
                  alt=""
                  className="w-full h-full object-contain"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-gray-900 text-sm break-words leading-snug">{c.name}</h3>
              {c.description && (
                <p className="text-xs text-gray-500 mt-0.5 break-words leading-snug">{c.description}</p>
              )}
              {c.url && (
                <a
                  href={c.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 mt-2 text-[11px] font-semibold text-emerald-700 hover:text-emerald-800"
                  data-testid={`sa-cobranding-url-${c.cobranding_id}`}
                >
                  Learn more <ArrowRight size={10} />
                </a>
              )}
            </div>
          </div>
        </article>
      ))}
    </section>
  );
}

/* ========== Recent Activity ========== */
function fmtRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

function actorLabel(e) {
  if (e.actor_email) return e.actor_email;
  if (e.assigned_by === "self") return "self-redeemed";
  if (e.assigned_by === "admin") return "global admin";
  if (e.assigned_by === "segment_admin") return "segment admin";
  return "—";
}

function RecentActivityStrip({ activity }) {
  return (
    <section data-testid="sa-recent-activity">
      <div className="flex items-center gap-2 mb-2">
        <Activity size={12} className="text-emerald-600" />
        <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Recent activity</p>
      </div>
      {(!activity || activity.length === 0) ? (
        <div className="bg-white border border-dashed border-gray-200 rounded-2xl p-5 text-center text-xs text-gray-400">
          No activity yet — assigned & revoked events will show up here.
        </div>
      ) : (
        <ol className="bg-white border border-gray-100 rounded-2xl divide-y divide-gray-50">
          {activity.map((e, i) => {
            const isEnroll = e.type === "enrolled";
            const Icon = isEnroll ? UserPlus : UserMinus;
            return (
              <li
                key={`${e.type}-${e.user_id}-${e.at}-${i}`}
                className="flex items-center gap-3 px-4 py-3"
                data-testid={`sa-activity-row-${i}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isEnroll ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"
                }`}>
                  <Icon size={13} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-900 truncate">
                    <span className="font-semibold">{e.member_email || e.member_name || "—"}</span>
                    <span className="text-gray-500"> {isEnroll ? "enrolled" : "revoked"}</span>
                    {e.code && <span className="font-mono text-[10px] text-gray-400 ml-1.5">({e.code})</span>}
                  </p>
                  <p className="text-[10px] text-gray-400 mt-0.5 truncate">
                    by <span className="font-medium text-gray-500">{actorLabel(e)}</span>
                  </p>
                </div>
                <span className="text-[10px] text-gray-400 flex-shrink-0 whitespace-nowrap">{fmtRelative(e.at)}</span>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

/* ========== Assign Code ========== */
function AssignTab({ onAssigned }) {
  const [email, setEmail] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [authCode, setAuthCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [tags, setTags] = useState([{ label: "", value: "" }]);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    if (!email || email.length < 2) { setResults([]); return; }
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await fetch(`${API}/sa/users/search?email=${encodeURIComponent(email)}`, { credentials: "include" });
        if (r.ok) setResults(await r.json());
      } catch { /* silent */ }
      finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(handle);
  }, [email]);

  const reset = () => {
    setPicked(null); setAuthCode(""); setVerified(false); setTags([{ label: "", value: "" }]); setEmail(""); setResults([]);
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const r = await fetch(`${API}/sa/verify-auth-code`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: picked.user_id, auth_code: authCode.trim().toUpperCase() }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Failed");
      setVerified(true);
      toast.success("Authorisation code verified");
    } catch (e) {
      toast.error(e.message || "Code verification failed");
    } finally { setVerifying(false); }
  };

  const assign = async () => {
    const cleanTags = tags.filter((t) => t.label.trim() && t.value.trim()).map((t) => ({ label: t.label.trim(), value: t.value.trim() }));
    setAssigning(true);
    try {
      const r = await fetch(`${API}/sa/assign-code`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: picked.user_id, auth_code: authCode.trim().toUpperCase(), tags: cleanTags }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Failed");
      toast.success(`Assigned code ${data.code} to ${picked.email}`);
      reset();
      onAssigned?.();
    } catch (e) {
      toast.error(e.message || "Failed to assign");
    } finally { setAssigning(false); }
  };

  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-5 space-y-5 max-w-2xl" data-testid="sa-assign-tab">
      {/* 1. Search */}
      <div>
        <Label className="text-xs font-semibold text-gray-700 mb-1.5 block">1. Find Customer (login email)</Label>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={picked ? picked.email : email}
            onChange={(e) => { setEmail(e.target.value); setPicked(null); setVerified(false); }}
            placeholder="user@example.com"
            className="pl-9"
            data-testid="sa-search-email"
            disabled={!!picked}
          />
          {picked && (
            <button type="button" onClick={reset} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500" data-testid="sa-search-clear">
              <X size={14} />
            </button>
          )}
        </div>
        {!picked && results.length > 0 && (
          <div className="mt-2 border border-gray-100 rounded-lg divide-y divide-gray-50 max-h-56 overflow-y-auto">
            {results.map((u) => (
              <button
                type="button"
                key={u.user_id}
                onClick={() => { setPicked(u); setResults([]); setEmail(u.email); }}
                className="w-full text-left px-3 py-2 hover:bg-emerald-50"
                data-testid={`sa-search-option-${u.user_id}`}
              >
                <p className="text-sm font-medium text-gray-900">{u.name || "—"}</p>
                <p className="text-[11px] text-gray-500">{u.email}</p>
              </button>
            ))}
          </div>
        )}
        {!picked && searching && <p className="text-xs text-gray-400 mt-1"><Loader2 size={11} className="inline animate-spin mr-1" /> Searching…</p>}
      </div>

      {/* 2. Auth code */}
      {picked && (
        <div className="border-t border-gray-100 pt-5">
          <Label className="text-xs font-semibold text-gray-700 mb-1.5 block">2. Customer's Authorization Code</Label>
          <p className="text-[11px] text-gray-500 mb-2">Ask <b>{picked.email}</b> to share their 6-character code from their Profile → Authorization Code section.</p>
          <div className="flex items-center gap-2">
            <Input
              value={authCode}
              onChange={(e) => { setAuthCode(e.target.value.toUpperCase()); setVerified(false); }}
              placeholder="ABC123"
              maxLength={8}
              className="font-mono tracking-[0.4em] uppercase"
              disabled={verified}
              data-testid="sa-auth-code-input"
            />
            {!verified ? (
              <Button onClick={verify} disabled={verifying || !authCode.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="sa-verify-btn">
                {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck size={14} />}
              </Button>
            ) : (
              <span className="inline-flex items-center text-xs font-bold text-emerald-700"><CheckCircle size={14} className="mr-1" /> Verified</span>
            )}
          </div>
        </div>
      )}

      {/* 3. Tags */}
      {picked && verified && (
        <div className="border-t border-gray-100 pt-5">
          <Label className="text-xs font-semibold text-gray-700 mb-1.5 block">3. Identification Tags <span className="text-gray-400 font-normal">(employee number, dept, etc — optional)</span></Label>
          <div className="space-y-2">
            {tags.map((t, idx) => (
              <div key={idx} className="flex items-center gap-2" data-testid={`sa-tag-row-${idx}`}>
                <Input
                  value={t.label}
                  onChange={(e) => setTags((prev) => prev.map((x, i) => i === idx ? { ...x, label: e.target.value } : x))}
                  placeholder="Label (e.g. Employee #)"
                  className="text-xs"
                />
                <Input
                  value={t.value}
                  onChange={(e) => setTags((prev) => prev.map((x, i) => i === idx ? { ...x, value: e.target.value } : x))}
                  placeholder="Value (e.g. TCS-12345)"
                  className="text-xs"
                />
                {tags.length > 1 && (
                  <button type="button" onClick={() => setTags((prev) => prev.filter((_, i) => i !== idx))} className="text-gray-300 hover:text-red-500">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
            <button type="button" onClick={() => setTags((prev) => [...prev, { label: "", value: "" }])} className="text-xs text-emerald-600 font-semibold" data-testid="sa-add-tag">
              + Add tag
            </button>
          </div>
        </div>
      )}

      {/* Submit */}
      {picked && verified && (
        <div className="border-t border-gray-100 pt-5">
          <Button onClick={assign} disabled={assigning} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="sa-assign-btn">
            {assigning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Assigning…</> : <><BadgeCheck size={14} className="mr-2" /> Assign Next Available Code</>}
          </Button>
        </div>
      )}
    </div>
  );
}

/* ========== Members ========== */
function MembersTab({ members, onChanged }) {
  const [showRevoked, setShowRevoked] = useState(false);
  const list = members.filter((m) => showRevoked ? m.is_revoked : !m.is_revoked);

  const revoke = async (m) => {
    if (!confirm(`Revoke segment access for ${m.email}? They will be notified.`)) return;
    try {
      const r = await fetch(`${API}/sa/members/${m.assignment_id}/revoke`, { method: "POST", credentials: "include" });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Failed");
      }
      toast.success("Member revoked");
      onChanged?.();
    } catch (e) {
      toast.error(e.message || "Failed to revoke");
    }
  };

  const sourceBadge = (src) => {
    if (src === "self") return <span className="text-[9px] font-bold uppercase bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Self</span>;
    if (src === "admin") return <span className="text-[9px] font-bold uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Admin</span>;
    if (src === "segment_admin") return <span className="text-[9px] font-bold uppercase bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Segment Admin</span>;
    return <span className="text-[9px] font-bold uppercase bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">Legacy</span>;
  };

  return (
    <div className="space-y-4" data-testid="sa-members-tab">
      <div className="flex items-center gap-2">
        <button
          onClick={() => setShowRevoked(false)}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold ${!showRevoked ? "bg-emerald-600 text-white" : "bg-white border border-gray-200 text-gray-600"}`}
          data-testid="sa-tab-active"
        >
          Active ({members.filter((m) => !m.is_revoked).length})
        </button>
        <button
          onClick={() => setShowRevoked(true)}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold ${showRevoked ? "bg-red-600 text-white" : "bg-white border border-gray-200 text-gray-600"}`}
          data-testid="sa-tab-revoked"
        >
          Revoked ({members.filter((m) => m.is_revoked).length})
        </button>
      </div>

      {list.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-2xl p-8 text-center text-gray-400 text-sm">
          No {showRevoked ? "revoked" : "active"} members yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {list.map((m) => (
            <div key={m.assignment_id} className="bg-white border border-gray-100 rounded-2xl p-4" data-testid={`sa-member-card-${m.assignment_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-gray-900 truncate">{m.name || "—"}</p>
                    {sourceBadge(m.assigned_by)}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5 break-all">{m.email}</p>
                  {m.code && <p className="text-[10px] font-mono text-gray-400 mt-1">code: {m.code}</p>}
                </div>
                {!showRevoked && (
                  <button
                    type="button"
                    onClick={() => revoke(m)}
                    className="text-red-400 hover:text-red-600 p-1.5"
                    title="Revoke"
                    data-testid={`sa-revoke-${m.assignment_id}`}
                  >
                    <XCircle size={16} />
                  </button>
                )}
              </div>
              {m.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2.5 pt-2.5 border-t border-gray-50">
                  {m.tags.map((t, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-700"
                      data-testid="sa-member-tag"
                    >
                      <Tag size={9} className="text-gray-400" />
                      <b>{t.label}:</b> {t.value}
                    </span>
                  ))}
                </div>
              )}
              {m.is_revoked && m.revoked_at && (
                <p className="text-[10px] text-red-500 mt-2">Revoked on {new Date(m.revoked_at).toLocaleDateString()}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ========== Revenue ========== */
function RevenueTab() {
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    fetch(`${API}/sa/revenue/users`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : [])
      .then((data) => setUsers(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (userId && userId !== "all") params.set("user_id", userId);
      const r = await fetch(`${API}/sa/revenue?${params.toString()}`, { credentials: "include" });
      if (!r.ok) throw new Error((await r.json()).detail || "Failed");
      setReport(await r.json());
    } catch (e) {
      toast.error(e.message || "Failed to load revenue");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const sym = report?.summary?.currency || "₹";
  const top = report?.top_stats || { total_sales: 0, sales_today: 0, sales_this_week: 0 };

  return (
    <div className="space-y-5" data-testid="sa-revenue">
      {/* Top blocks (segment-wide) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <RevBlock icon={<TrendingUp size={14} />} label="Total Sales" value={`${sym}${Math.round(top.total_sales)}`} color="bg-emerald-50 text-emerald-700 border-emerald-100" testid="rev-total" />
        <RevBlock icon={<IndianRupee size={14} />} label="Sales Today" value={`${sym}${Math.round(top.sales_today)}`} color="bg-blue-50 text-blue-700 border-blue-100" testid="rev-today" />
        <RevBlock icon={<BarChart3 size={14} />} label="Sales This Week" value={`${sym}${Math.round(top.sales_this_week)}`} color="bg-amber-50 text-amber-700 border-amber-100" testid="rev-week" />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 sm:items-end">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1 block">Login ID</label>
            <UserPicker
              users={users}
              value={userId}
              onChange={setUserId}
              data-testid="rev-user"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1 block">From</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="text-xs" data-testid="rev-from" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1 block">To</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="text-xs" data-testid="rev-to" />
          </div>
          <Button onClick={load} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" data-testid="rev-search">
            {loading ? <Loader2 size={13} className="animate-spin" /> : <><Search size={13} className="mr-1" /> Search</>}
          </Button>
        </div>
      </div>

      {/* Filtered summary — only 2 cards */}
      {report && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <SmallStat icon={<Package size={14} />} label="Total Orders" value={report.summary.total_orders} color="#3B82F6" />
            <SmallStat icon={<TrendingUp size={14} />} label="Total Sales" value={`${sym}${Math.round(report.summary.total_revenue)}`} color="#10B981" />
          </div>

          {/* Daily breakdown */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="p-3 border-b border-gray-100">
              <h2 className="text-sm font-bold text-gray-900">Daily Breakdown</h2>
              <p className="text-[10px] text-gray-400 mt-0.5">
                {userId === "all"
                  ? "All segment members"
                  : `Filtered to ${(users.find((u) => u.user_id === userId) || {}).email || userId}`}
                {(dateFrom || dateTo) && <> · {dateFrom || "…"} to {dateTo || "…"}</>}
                <span className="ml-1">· values exclude tax, after discount</span>
              </p>
            </div>
            {report.daily_breakdown.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm">No orders found for this filter.</div>
            ) : (
              report.daily_breakdown.map((day) => (
                <div key={day.date} className="border-b border-gray-100 last:border-0">
                  <button
                    onClick={() => setExpanded((p) => ({ ...p, [day.date]: !p[day.date] }))}
                    className="w-full flex items-center justify-between p-3 hover:bg-gray-50"
                    data-testid={`rev-day-${day.date}`}
                  >
                    <div className="flex items-center gap-3">
                      <Calendar size={14} className="text-gray-400" />
                      <div className="text-left">
                        <p className="text-sm font-semibold text-gray-900">
                          {new Date(day.date + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                        </p>
                        <p className="text-[10px] text-gray-500">{day.order_count} orders</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-emerald-600">{sym}{Math.round(day.total_revenue)}</span>
                      {expanded[day.date] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </div>
                  </button>
                  {expanded[day.date] && (
                    <div className="px-3 pb-3">
                      {/* Desktop table */}
                      <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-gray-500 border-b border-gray-100">
                              <th className="pb-2 text-left font-medium">Order #</th>
                              <th className="pb-2 text-left font-medium">Customer</th>
                              <th className="pb-2 text-left font-medium">Email</th>
                              <th className="pb-2 text-right font-medium">Subtotal</th>
                              <th className="pb-2 text-right font-medium">Discount</th>
                              <th className="pb-2 text-right font-medium">Net</th>
                              <th className="pb-2 text-center font-medium">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {day.orders.map((o) => (
                              <tr key={o.order_id} className="border-b border-gray-50 last:border-0">
                                <td className="py-2 font-semibold text-emerald-600">{o.order_number || o.order_id.slice(0, 12)}</td>
                                <td className="py-2 text-gray-700">{o.user_name || "—"}</td>
                                <td className="py-2 text-gray-500 break-all max-w-[180px]">{o.user_email || "—"}</td>
                                <td className="py-2 text-right text-gray-700">{sym}{Math.round(o.subtotal)}</td>
                                <td className="py-2 text-right text-gray-500">- {sym}{Math.round(o.discount_amount || 0)}</td>
                                <td className="py-2 text-right font-bold text-gray-900">{sym}{Math.round(o.net_amount)}</td>
                                <td className="py-2 text-center">
                                  <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold ${
                                    o.status === "delivered" ? "bg-green-100 text-green-700" :
                                    o.status === "cancelled" ? "bg-red-100 text-red-700" :
                                    "bg-yellow-100 text-yellow-700"
                                  }`}>{o.status}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {/* Mobile cards */}
                      <div className="md:hidden space-y-2">
                        {day.orders.map((o) => (
                          <div key={o.order_id} className="bg-gray-50 rounded-lg p-2.5">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-semibold text-emerald-600">{o.order_number || o.order_id.slice(0, 10)}</span>
                              <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                                o.status === "delivered" ? "bg-green-100 text-green-700" :
                                o.status === "cancelled" ? "bg-red-100 text-red-700" :
                                "bg-yellow-100 text-yellow-700"
                              }`}>{o.status}</span>
                            </div>
                            <p className="text-xs text-gray-700 font-medium">{o.user_name || "—"}</p>
                            {o.user_email && <p className="text-[10px] text-gray-500 break-all">{o.user_email}</p>}
                            <div className="flex items-center justify-between mt-1">
                              <span className="text-[10px] text-gray-500">
                                {sym}{Math.round(o.subtotal)} − {sym}{Math.round(o.discount_amount || 0)}
                              </span>
                              <span className="text-sm font-bold text-gray-900">{sym}{Math.round(o.net_amount)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* Searchable user picker — works reliably on mobile WebViews where native <select> is awkward. */
function UserPicker({ users, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = users.find((u) => u.user_id === value);
  const filtered = q
    ? users.filter((u) => (u.email || "").toLowerCase().includes(q.toLowerCase()) || (u.name || "").toLowerCase().includes(q.toLowerCase()))
    : users;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full flex items-center justify-between gap-2 text-xs border border-gray-200 rounded-md px-2.5 py-2 bg-white hover:border-emerald-300 transition-colors"
        data-testid="rev-user"
      >
        <span className="truncate text-left">
          {value === "all" ? `All segment users (${users.length})` : (selected?.email || "Select user")}
        </span>
        <ChevronDown size={12} className="text-gray-400 flex-shrink-0" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-sm">Select Login ID</DialogTitle>
          </DialogHeader>
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by email or name…"
              className="pl-8 text-xs"
              data-testid="rev-user-search"
            />
          </div>
          <div className="overflow-y-auto -mx-6 px-6 flex-1">
            <button
              type="button"
              onClick={() => { onChange("all"); setOpen(false); setQ(""); }}
              className={`w-full text-left px-3 py-2.5 rounded-lg text-xs flex items-center justify-between hover:bg-emerald-50 ${value === "all" ? "bg-emerald-50 text-emerald-700 font-bold" : "text-gray-700"}`}
              data-testid="rev-user-option-all"
            >
              <span>All segment users ({users.length})</span>
              {value === "all" && <CheckCircle size={13} className="text-emerald-600" />}
            </button>
            {filtered.length === 0 && (
              <p className="text-center text-xs text-gray-400 py-6">No matching users.</p>
            )}
            {filtered.map((u) => (
              <button
                type="button"
                key={u.user_id}
                onClick={() => { onChange(u.user_id); setOpen(false); setQ(""); }}
                className={`w-full text-left px-3 py-2.5 rounded-lg text-xs hover:bg-emerald-50 flex items-center justify-between ${value === u.user_id ? "bg-emerald-50 text-emerald-700" : "text-gray-700"}`}
                data-testid={`rev-user-option-${u.user_id}`}
              >
                <span className="min-w-0 flex-1">
                  <span className="block font-medium truncate">{u.email}</span>
                  {(u.name || !u.is_active_member) && (
                    <span className="block text-[10px] text-gray-400 truncate">
                      {u.name}{!u.is_active_member && (u.name ? " · " : "")}{!u.is_active_member && "revoked"}
                    </span>
                  )}
                </span>
                {value === u.user_id && <CheckCircle size={13} className="text-emerald-600 flex-shrink-0 ml-2" />}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function RevBlock({ icon, label, value, color, testid }) {
  return (
    <div className={`rounded-2xl border p-4 ${color}`} data-testid={testid}>
      <div className="flex items-center gap-1.5">
        <div className="opacity-70">{icon}</div>
        <span className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</span>
      </div>
      <p className="text-2xl md:text-3xl font-extrabold mt-1.5">{value}</p>
    </div>
  );
}

function SmallStat({ icon, label, value, color }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-3" data-testid={`rev-stat-${label.toLowerCase()}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <div style={{ color }}>{icon}</div>
        <span className="text-[10px] text-gray-500 font-medium">{label}</span>
      </div>
      <p className="text-base sm:text-lg font-bold text-gray-900">{value}</p>
    </div>
  );
}
